import { apiBoost, apiMetrics } from "@/common/services";
import {
  AddedToCollectionEvent,
  BoostProfileClickedEvent,
  DailyVisitEvent,
  FollowUserClickedEvent,
  GifLikedEvent,
  GifPlaybackEvent,
  GifSharedEvent,
  GifViewEvent,
  LiveCircleClickedEvent,
  ProfileClickedEvent,
} from "@/common/buses";

enum EventTypes {
  gifView = "gifView",
  gifPlayback = "gifPlayback",
  gifShared = "gifShared",
  addedToCollection = "addedToCollection",
  gifLiked = "gifLiked",
  profileClicked = "profileClicked",
  followUserClicked = "followUserClicked",
  dailyVisit = "dailyVisit",
  liveCircleClicked = "liveCircleClicked",
  nicheSearched = "nicheSearched",
}

const CRITICAL_EVENTS = [
  EventTypes.gifView,
  EventTypes.gifPlayback,
  EventTypes.gifShared,
  EventTypes.dailyVisit,
  EventTypes.liveCircleClicked,
  EventTypes.nicheSearched,
  EventTypes.profileClicked,
];

const ALLOWED_EVENT_PATHS = [
  "/gifs/amateur",
  "/niches/amateur-girls"
];

class LocalMetrics {
  sendGifView(props: GifViewEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifView, () => {
      return apiMetrics.sendGifView({
        gif: props.gifId,
        position: props.position,
        source: props.source,
        source_id: props.sourceId,
        boosted: props.boosted,
        username: props.userName,
        fullscreen: props.fullscreen,
        pathname: location.pathname,
        context: props.context,
        reason: props.reason,
      });
    });
  }

  sendGifPlayback(props: GifPlaybackEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifPlayback, () => {
      return apiMetrics.sendGifPlayback({
        username: props.userName,
        duration: props.duration, // in ms
        gif: props.gif,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        start_position: props.startPoint,
        pathname: location.pathname,
      });
    });
  }

  sendGifShared(props: GifSharedEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifShared, () => {
      return apiMetrics.sendGifShared({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendGifAddedToCollection(props: AddedToCollectionEvent): Promise<void> {
    return this.sendEvent(EventTypes.addedToCollection, () => {
      return apiMetrics.sendGifAddedToCollection({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendGifLiked(props: GifLikedEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifLiked, () => {
      return apiMetrics.sendGifLiked({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendProfileClicked(props: ProfileClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.profileClicked, () => {
      return apiMetrics.sendProfileClicked({
        gif: props.gifId,
        creator: props.creator,
        source: props.source,
        source_id: props.sourceId,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendFollowUserClicked(props: FollowUserClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.followUserClicked, async () => {
      if (!props.gifId) {
        return;
      }

      await apiMetrics.sendUserSubscription({
        gif: props.gifId,
        username: props.userName,
        source: props.source,
        position: props.position,
        play_count: props.playCount,
        pathname: location.pathname,
      });
    });
  }

  sendDailyVisit(props: DailyVisitEvent): Promise<void> {
    return this.sendEvent(EventTypes.dailyVisit, () => {
      return apiMetrics.sendVisit(props.username);
    });
  }

  sendBoostProfileClicked(props: BoostProfileClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.dailyVisit, () => {
      return apiBoost.addProfileClick({
        campaignId: props.campaignId,
        pathname: location.pathname
      });
    });
  }

  sendLiveCircleClicked(props: LiveCircleClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.liveCircleClicked, () => {
      return apiMetrics.sendLiveCircleClicked({
        device: props.deviceType,
      });
    });
  }

  sendNicheSearched(niche: string): Promise<void> | void {
    return this.sendEvent(EventTypes.nicheSearched, () => {
      return apiMetrics.sendNicheSearched({ niche });
    });
  }

  private async sendEvent(eventType: EventTypes, request: () => Promise<void>) {
    if (!CRITICAL_EVENTS.includes(eventType) && !ALLOWED_EVENT_PATHS.includes(location.pathname)) {
      return;
    }

    try {
      return await request();
    } catch (err) {
      console.error("Error sending metrics.");
    }
  }
}

export default new LocalMetrics();
